import * as ModelAPI from "api/ModelAPI";

import * as runReducer from "containers/Run/reducer";
import * as setupReducer from "containers/Setup/reducer";
import * as modelGroupReducer from "containers/ModelGroup/reducer";
import * as accountReducer from "containers/Accounts/reducer";
import * as loginReducer from "containers/Login/reducer";
import * as homeReducer from "containers/Home/reducer";
import * as optimizerCreatorReducer from "containers/OptimizerCreator/reducer";

import { ConfusionMatrix } from "utils/stats";

export const INIT_OPTIMIZER = "optimizer/INIT_OPTIMIZER";
export const UPDATE_KEY = "optimizer/UPDATE_KEY";
export const UPDATE_SUB_KEY = "optimizer/UPDATE_SUB_KEY";
export const RESET = "optimizer/RESET";

const SalesConstraintType = {
  AllPositive: "ALL_POSITIVE",
  TruePositive: "TRUE_POSITIVE"
};

const initialState = {
  [getId(2833, 2860)]: {
    marketingRunId: 2833,
    salesRunId: 2860,
    firstRunId: 2833,
    secondRunId: 2860,
    drilldownColumn: "Region",
    salesDrilldown: "Type",
    marketingDrilldown: "Channel",
    drilldownValue: "Overall",
    overallRuns: {
      marketing: {
        US: "2839",
        EMEA: "2840",
        LATAM: "2841"
      },
      sales: {
        US: "2872",
        EMEA: "2871",
        LATAM: "2873"
      }
    },
    marketingRuns: {
      Other: "2825",
      Events: "2826",
      "Digital Marketing": "2827"
    },
    salesRuns: {
      Upsell: "2828",
      "New Customer": "2829"
    },
    combinedRuns: {
      US: {
        "Digital Marketing": {
          isMarketing: true,
          runId: "2810"
        },
        Events: {
          isMarketing: true,
          runId: "2809"
        },
        Other: {
          isMarketing: true,
          runId: "2814"
        },
        Upsell: {
          isMarketing: false,
          runId: "2823"
        },
        "New Customer": {
          isMarketing: false,
          runId: "2824"
        }
      },
      EMEA: {
        Events: {
          isMarketing: true,
          runId: "2813"
        },
        Other: {
          isMarketing: true,
          runId: "2811"
        },
        "Digital Marketing": {
          isMarketing: true,
          runId: "2815"
        },
        "New Customer": {
          isMarketing: false,
          runId: "2822"
        },
        Upsell: {
          isMarketing: false,
          runId: "2821"
        }
      },
      LATAM: {
        Events: {
          isMarketing: true,
          runId: "2818"
        },
        Other: {
          isMarketing: true,
          runId: "2816"
        },
        "Digital Marketing": {
          isMarketing: true,
          runId: "2817"
        },
        Upsell: {
          isMarketing: false,
          runId: "2819"
        },
        "New Customer": {
          isMarketing: false,
          runId: "2820"
        }
      }
    },
    salesConstraintType: "ALL_POSITIVE",
    salesBudget: "5220000000",
    marketingBudget: "938540000",
    overallScale: 1
  }
};

export function getId(firstRunId, secondRunId) {
  return firstRunId + "@_@" + secondRunId;
}

function optimizerReducer(state = initialState, action) {
  switch (action.type) {
    case INIT_OPTIMIZER:
      return {
        ...state,
        ...action.data
      };
    case UPDATE_KEY:
      return {
        ...state,
        [action.key]: action.value
      };
    case UPDATE_SUB_KEY:
      return {
        ...state,
        [action.key]: {
          ...state[action.key],
          [action.subKey]: action.value
        }
      };
    case RESET:
      return {
        ...initialState
      };
    default:
      return state;
  }
}

/* Selectors */
export function getBase(state) {
  return state.optimizer;
}

export function getOptimizer(state, id) {
  return getBase(state)[id];
}

export function getRegion(state, id) {
  return getOptimizer(state, id).region;
}

export function getOverallScale(state, id) {
  return getOptimizer(state, id).overallScale;
}

export function getOptimizers(state) {
  return Object.keys(state.optimizer).map((o) => state.optimizer[o]);
}

export function getMarketingUnitCost(state, id) {
  const marketingRunId = getMarketingRunId(state, id);

  return Math.abs(getOptimizedConversionCost(state, marketingRunId));
}

export function getSalesUnitCost(state, id) {
  const salesRunId = getSalesRunId(state, id);

  return Math.abs(getOptimizedConversionCost(state, salesRunId));
}

export function getSalesUnitRevenue(state, id) {
  const salesRunId = getSalesRunId(state, id);

  return getOptimizedConversionGain(state, salesRunId) + Math.abs(getOptimizedConversionCost(state, salesRunId));
}

export function getOptimizedConversionGain(state, runId) {
  return setupReducer.getConversionGain(state, runId, true);
}

export function getOptimizedConversionCost(state, runId) {
  return setupReducer.getConversionCost(state, runId, true);
}

export function getSalesUnitProfit(state, id) {
  return getSalesUnitRevenue(state, id) - getSalesUnitCost(state, id);
}

export function getSalesConstraintType(state, id) {
  return getOptimizer(state, id).salesConstraintType;
}

export function getMarketingBudget(state, id) {
  return Number(getOptimizer(state, id).marketingBudget);
}

export function getSalesBudget(state, id) {
  return Number(getOptimizer(state, id).salesBudget);
}

export function getTotalBudget(state, id) {
  return getMarketingBudget(state, id) + getSalesBudget(state, id);
}

export function getMarketingCapacity(state, id) {
  return Math.min(getMarketingBudget(state, id) / getMarketingUnitCost(state, id), getSalesCapacity(state, id));
}

export function getSalesCapacity(state, id) {
  return getSalesBudget(state, id) / getSalesUnitCost(state, id);
}

export function getTotalRevenue(state, id) {
  return getTotalSalesWon(state, id) * getSalesUnitRevenue(state, id);
}

export function getTotalCost(state, id) {
  return (
    getTotalSalesPursued(state, id) * getSalesUnitCost(state, id) +
    getTotalMarketingPursued(state, id) * getMarketingUnitCost(state, id)
  );
}

export function getProfit(state, id) {
  return getTotalRevenue(state, id) - getTotalCost(state, id);
}

export function getPredictedMarketingUnits(state, id) {
  const marketingCapacity = getMarketingCapacity(state, id);

  return getMarketingConfusionMatrix(state, id).copyAndConstrainPositivePredictions(marketingCapacity).getAllPositive();
}

export function getPredictedSalesUnits(state, id) {
  return getSalesConfusionMatrix(state, id)
    .copyAndScaleTotal(predictedMarketingUnits)
    .copyAndConstrainPositivePredictions(salesCapacity);
}

export function getMarketingConfusionMatrix(state, id, isNotScaled = false) {
  const runId = getMarketingRunId(state, id);
  const marketingModel = runReducer.getFirstModel(state, runId);

  const optimizer = getOptimizer(state, id);

  if (optimizer == undefined) {
    return undefined;
  }

  const firstRunId = optimizer.marketingRunId;
  const secondRunId = optimizer.salesRunId;

  let drilldownValues = [
    {
      value: "Overall",
      label: "Overall",
      firstRunId: optimizer.firstRunId,
      secondRunId: optimizer.secondRunId,
      pctOfRows: 1
    }
  ];

  Object.keys(optimizer.overallRuns.marketing).forEach((m) =>
    drilldownValues.push({
      value: m,
      label: m,
      firstRunId: optimizer.overallRuns.marketing[m],
      secondRunId: optimizer.overallRuns.sales[m],
      pctOfRows: runReducer.getPctOfRows(state, optimizer.overallRuns.marketing[m])
    })
  );

  let pctOfRows = 1.0;

  drilldownValues.forEach((d) => {
    if (d.firstRunId === firstRunId) {
      pctOfRows = d.pctOfRows;
    }
  });

  const overallScale = getOverallScale(state, id);

  const cm = getConfusionMatrixFromModel(marketingModel);

  return isNotScaled
    ? cm
    : cm.copyAndScaleTotal(cm.getTotal() * pctOfRows).copyAndScaleTotal(cm.getTotal() * overallScale);
}

export function getSalesConfusionMatrix(state, id) {
  const runId = getSalesRunId(state, id);
  const salesModel = runReducer.getFirstModel(state, runId);

  return getConfusionMatrixFromModel(salesModel);
}

function getConfusionMatrixFromModel(model) {
  const { truePositive, falsePositive, falseNegative, trueNegative } = model.maxIteration;

  return new ConfusionMatrix(truePositive, trueNegative, falsePositive, falseNegative);
}

export function getConstrainedMarketingConfusionMatrix(state, id) {
  const marketingCapacity = getMarketingCapacity(state, id);

  return getMarketingConfusionMatrix(state, id).copyAndConstrainPositivePredictions(marketingCapacity);
}

export function getTotalMarketingProspects(state, id) {
  return getMarketingConfusionMatrix(state, id).getTotal();
}

export function getTotalMarketingPursued(state, id) {
  return getConstrainedMarketingConfusionMatrix(state, id).getAllPositive();
}

export function getTotalMarketingWon(state, id) {
  return getConstrainedMarketingConfusionMatrix(state, id).getTruePositive();
}

export function getTotalSalesConstraint(state, id) {
  const mcm = getConstrainedMarketingConfusionMatrix(state, id);

  return mcm.getTruePositive();

  /*

  switch (getSalesConstraintType(state, id)) {
    case SalesConstraintType.TruePositive:
      return mcm.getTruePositive();

    case SalesConstraintType.AllPositive:
    default:
      return mcm.getAllPositive();
  }
  */
}

export function getConstrainedSalesConfusionMatrix(state, id) {
  let totalSalesConstraint = getTotalSalesConstraint(state, id);
  const salesCapacity = getSalesCapacity(state, id);

  return getSalesConfusionMatrix(state, id)
    .copyAndScaleTotal(totalSalesConstraint)
    .copyAndConstrainPositivePredictions(salesCapacity);
}

export function getTotalSalesPursued(state, id) {
  return getConstrainedSalesConfusionMatrix(state, id).getAllPositive();
}

export function getTotalSalesWon(state, id) {
  return getConstrainedSalesConfusionMatrix(state, id).getTruePositive();
}

export function getMarketingRunId(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).marketingRunId : undefined;
}

export function getSalesRunId(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).salesRunId : undefined;
}

export function getDrilldownColumn(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).drilldownColumn : undefined;
}

export function getDrilldownValue(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).drilldownValue : undefined;
}

export function getSalesDrilldown(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).salesDrilldown : undefined;
}

export function getMarketingDrilldown(state, id) {
  return getOptimizer(state, id) ? getOptimizer(state, id).marketingDrilldown : undefined;
}

export function getBreakEvenProbability(state, id) {
  const salesRunId = getSalesRunId(state, id);
  const conversionCost = getOptimizedConversionCost(state, salesRunId);
  const conversionGain = getOptimizedConversionGain(state, salesRunId);
  const processLimitPct = setupReducer.getProcessLimitPct(state, salesRunId);

  const breakEvenProb = -conversionCost / (-conversionCost + conversionGain);

  const retBreakEvenProb = breakEvenProb + (1 - breakEvenProb) * (1 - processLimitPct);

  return retBreakEvenProb;
}

export function getCurrentProfit(state, id) {
  return (
    getCurrentRevenue(state, id) -
    (getCurrentConfusionMatrix(state, getMarketingRunId(state, id)).getAllPositive() * getMarketingUnitCost(state, id) +
      getCurrentConfusionMatrix(state, getSalesRunId(state, id)).getAllPositive() * getSalesUnitCost(state, id))
  );
}

export function getCurrentRevenue(state, id) {
  return getCurrentConfusionMatrix(state, getSalesRunId(state, id)).getTruePositive() * getSalesUnitRevenue(state, id);
}

export function getCurrentConfusionMatrix(state, runId) {
  return new ConfusionMatrix(
    setupReducer.getTruePositive(state, runId),
    setupReducer.getTrueNegative(state, runId),
    setupReducer.getFalsePositive(state, runId),
    setupReducer.getFalseNegative(state, runId)
  );
}

export function getErrorRuns(state, id) {
  const optimizer = getOptimizer(state, id);

  if (optimizer === undefined) {
    return undefined;
  }

  let errorRuns = {};

  const jobKeys = getAllJobsFromOptimizer(state, optimizer.firstRunId, optimizer.secondRunId);

  jobKeys.forEach((k) => {
    const job = runReducer.getJob(state, k);

    errorRuns[k] = job ? job.status : undefined;
  });

  return errorRuns;
}

export function getGroupInfo(state, id) {
  const optimizer = getOptimizer(state, id);

  if (optimizer === undefined) {
    return undefined;
  }

  let retInfo = {
    marketing: {},
    sales: {}
  };

  Object.keys(optimizer.combinedRuns).forEach((v) => {
    const overallMarketingId = optimizer.overallRuns.marketing[v];

    retInfo.marketing[v] = {};
    retInfo.sales[v] = {};

    Object.keys(optimizer.combinedRuns[v]).forEach((s) => {
      const runId = optimizer.combinedRuns[v][s].runId;

      const pctOfRows = runReducer.getPctOfRows(state, runId);
      const rowCount = runReducer.getRowCount(state, runId);
      const bestModel = runReducer.getFirstModel(state, runId);
      const cm = bestModel ? getConfusionMatrixFromModel(bestModel).copyAndScaleTotal(rowCount) : undefined;
      const profit = cm ? cm.getTruePositive() * getOptimizedConversionGain(state, runId) : undefined;
      const ppr = profit ? profit / pctOfRows : 0;

      if (optimizer.combinedRuns[v][s].isMarketing) {
        retInfo.marketing[v][s] = {
          isMarketing: optimizer.combinedRuns[v][s].isMarketing,
          bestModel: bestModel,
          cm: cm,
          pctOfRows: pctOfRows,
          rowCount: rowCount,
          profit: cm ? cm.getTruePositive() * getOptimizedConversionGain(state, runId) : 0,
          cost: cm ? cm.getAllPositive() * getOptimizedConversionCost(state, runId) : 0,
          ppr: ppr
        };
      } else {
        retInfo.sales[v][s] = {
          isMarketing: optimizer.combinedRuns[v][s].isMarketing,
          bestModel: bestModel,
          cm: cm,
          pctOfRows: pctOfRows,
          rowCount: rowCount,
          profit: cm ? cm.getTruePositive() * getOptimizedConversionGain(state, runId) : 0,
          cost: cm ? cm.getAllPositive() * getOptimizedConversionCost(state, runId) : 0,
          ppr: ppr
        };
      }
    });
  });

  return retInfo;
}

export function getModelGroups(state, id) {
  const firstRunId = getMarketingRunId(state, id);
  const secondRunId = getSalesRunId(state, id);
  const optimizer = getOptimizer(state, id);

  if (optimizer === undefined) {
    return undefined;
  }

  let modelGroups = {
    first: modelGroupReducer.getModelGroup(state, firstRunId),
    second: modelGroupReducer.getModelGroup(state, secondRunId)
  };

  modelGroups.hasGroup = true;
  modelGroups.drilldownColumn = optimizer.drilldownColumn;

  modelGroups.drilldownValues = [
    {
      value: "Overall",
      label: "Overall",
      firstRunId: optimizer.firstRunId,
      secondRunId: optimizer.secondRunId,
      pctOfRows: 1
    }
  ];

  Object.keys(optimizer.overallRuns.marketing).forEach((m) =>
    modelGroups.drilldownValues.push({
      value: m,
      label: m,
      firstRunId: optimizer.overallRuns.marketing[m],
      secondRunId: optimizer.overallRuns.sales[m]
    })
  );

  modelGroups.drilldownValues.forEach((d) => {
    d.firstSetup = setupReducer.getSetup(state, d.firstRunId, true);
    d.secondSetup = setupReducer.getSetup(state, d.secondRunId, true);
    d.pctOfRows = runReducer.getPctOfRows(state, d.firstRunId);
  });

  modelGroups.topDrivers = {
    marketing: modelGroups.drilldownValues.map((v) => ({
      drilldownValue: v.value,
      drilldownColumn: modelGroups.drilldownColumn,
      driver: getTopDriver(state, v.firstRunId)
    })),
    sales: modelGroups.drilldownValues.map((v) => ({
      drilldownValue: v.value,
      drilldownColumn: modelGroups.drilldownColumn,
      driver: getTopDriver(state, v.secondRunId)
    }))
  };

  return modelGroups;
}

export function getTopDriver(state, runId) {
  const firstModel = runReducer.getFirstModel(state, runId);

  if (firstModel === undefined || firstModel.features === undefined || firstModel.features.percentages === undefined) {
    return undefined;
  }

  const setup = setupReducer.getSetup(state, runId, true);

  const profit =
    firstModel.maxIteration.truePositive * setup.conversionGain -
    firstModel.maxIteration.falsePositive * setup.conversionCost;

  let topFeature = undefined;
  let topFeatureName = undefined;

  let retFeatures = [];

  Object.keys(firstModel.features.percentages).forEach((p) =>
    Object.keys(firstModel.features.percentages[p].values).forEach((v) => {
      retFeatures.push({
        name: p + ": " + v,
        value: firstModel.features.percentages[p].values[v],
        profit: profit,
        score: Math.abs(profit * firstModel.features.percentages[p].values[v])
      });
    })
  );

  return retFeatures.sort((a, b) => b.score - a.score);
}

export function handleCreateDrilldown(id, drilldownColumn) {
  return (dispatch, getState) => {
    const state = getState();

    const firstRunId = getMarketingRunId(state, id);
    const secondRunId = getSalesRunId(state, id);

    const firstRun = runReducer.getJob(state, firstRunId);
    const secondRun = runReducer.getJob(state, secondRunId);

    const firstDataset = homeReducer.getDataset(state, firstRun.datasetId);
    const secondDataset = homeReducer.getDataset(state, secondRun.datasetId);

    const retModelGroups = {
      models: firstDataset.fields[drilldownColumn.value].data.map((c) => ({
        job_id: null,
        model_name: null,
        conditions: [{ column: drilldownColumn.value, value: c.value, operand: "=" }]
      }))
    };

    /*
    const retModelGroups = {
      models: [
        {
          job_id: null,
          model_name: null,
          conditions: specialVals
            ? specialVals.map(s => ({ column: s.field, value: s.fieldValue, operand: "=" }))
            : []
        },
        ...modelGroup.models
      ]
    };

    return ModelGroupAPI.patchModelGroup(modelGroup.id, retModelGroups).then(response =>
      dispatch(updateModelGroup(originalId, transformModelGroup(response)))
    );
    */
  };
}

export function updateDrilldownValue(id, val) {
  return (dispatch, getState) => {
    dispatch(setMarketingRunId(id, val.firstRunId));
    dispatch(setSalesRunId(id, val.secondRunId));
    dispatch(setDrilldownValue(id, val.value));
  };
}

export function getOptimized(state, id) {
  const optimizer = getOptimizer(state, id);

  if (optimizer == undefined || hasLoadedAllJobs(state, id) === false) {
    return undefined;
  }

  const firstRunId = optimizer.marketingRunId;
  const secondRunId = optimizer.salesRunId;

  if (
    getOptimizer(state, id) === undefined ||
    Object.keys(runReducer.getJob(state, firstRunId)).length === 0 ||
    Object.keys(runReducer.getJob(state, secondRunId)).length === 0
  ) {
    return undefined;
  }

  return {
    first: {
      label: "Marketing",
      id: firstRunId,
      unitCost: getMarketingUnitCost(state, id),
      budget: getMarketingBudget(state, id),
      capacity: getMarketingCapacity(state, id),
      confusionMatrix: getMarketingConfusionMatrix(state, id),
      constrainedConfusionMatrix: getConstrainedMarketingConfusionMatrix(state, id),
      totalWon: getTotalMarketingWon(state, id),
      totalPursued: getTotalMarketingPursued(state, id),
      totalProspects: getTotalMarketingProspects(state, id),
      totalCost: getTotalMarketingPursued(state, id) * getMarketingUnitCost(state, id),
      bestModel: runReducer.getFirstModel(state, firstRunId),
      currentSpend: getCurrentConfusionMatrix(state, firstRunId).getAllPositive() * getMarketingUnitCost(state, id),
      setup: setupReducer.getSetup(state, firstRunId, true),
      dataset: setupReducer.getDatasetFromSetup(state, firstRunId)
    },
    second: {
      label: "Sales",
      id: secondRunId,
      unitCost: getSalesUnitCost(state, id),
      budget: getSalesBudget(state, id),
      capacity: getSalesCapacity(state, id),
      confusionMatrix: getSalesConfusionMatrix(state, id),
      constrainedConfusionMatrix: getConstrainedSalesConfusionMatrix(state, id),
      totalWon: getTotalSalesWon(state, id),
      totalPursued: getTotalSalesPursued(state, id),
      breakEvenProb: getBreakEvenProbability(state, id),
      totalCost: getTotalSalesPursued(state, id) * getSalesUnitCost(state, id),
      bestModel: runReducer.getFirstModel(state, secondRunId),
      currentSpend: getCurrentConfusionMatrix(state, secondRunId).getAllPositive() * getSalesUnitCost(state, id),
      setup: setupReducer.getSetup(state, secondRunId, true),
      dataset: setupReducer.getDatasetFromSetup(state, secondRunId)
    },
    profit: getProfit(state, id),
    revenue: getTotalRevenue(state, id),
    totalBudget: getTotalBudget(state, id),
    totalCost: getTotalCost(state, id),

    currentRevenue: getCurrentRevenue(state, id),
    revenueDelta: getTotalRevenue(state, id) - getCurrentRevenue(state, id),

    currentProfit: getCurrentProfit(state, id),
    profitDelta: getProfit(state, id) - getCurrentProfit(state, id)
  };
}

/* Actions */

export function updateBase(base) {}

export function setOptimizer(id, optimizer) {
  return (dispatch, getState) => {
    dispatch({ type: UPDATE_KEY, key: id, value: optimizer });

    return dispatch(saveOptimizerStorage());
  };
}

export function updateOverallRun(id, isMarketing, value, valueRunId) {
  return (dispatch, getState) => {
    let optimizer = { ...getOptimizer(getState(), id) };

    if (isMarketing === true) {
      optimizer.overallRuns.marketing[value] = valueRunId;
    } else {
      optimizer.overallRuns.sales[value] = valueRunId;
    }

    dispatch(setOptimizer(id, optimizer));
  };
}

export function updateSalesRun(id, value, valueRunId) {
  return (dispatch, getState) => {
    let optimizer = { ...getOptimizer(getState(), id) };

    optimizer.salesRuns[value] = valueRunId;

    dispatch(setOptimizer(id, optimizer));
  };
}

export function updateMarketingRun(id, value, valueRunId) {
  return (dispatch, getState) => {
    let optimizer = { ...getOptimizer(getState(), id) };

    optimizer.marketingRuns[value] = valueRunId;

    dispatch(setOptimizer(id, optimizer));
  };
}

export function updateCombined(id, isMarketing, first, second, runId) {
  return (dispatch, getState) => {
    let optimizer = { ...getOptimizer(getState(), id) };

    optimizer.combinedRuns[first][second] = {
      isMarketing: isMarketing,
      runId: runId
    };

    dispatch(setOptimizer(id, optimizer));
  };
}

export function setRegion(id, region) {
  return { type: UPDATE_SUB_KEY, subKey: "region", id: id, value: region };
}

export function setSalesConstraintType(id, salesConstraintType) {
  return {
    type: UPDATE_SUB_KEY,
    key: id,
    subKey: "salesConstraintType",
    value: salesConstraintType
  };
}

export function setMarketingBudget(id, marketingBudget) {
  return {
    type: UPDATE_SUB_KEY,
    key: id,
    subKey: "marketingBudget",
    value: marketingBudget
  };
}

export function setSalesBudget(id, salesBudget) {
  return {
    type: UPDATE_SUB_KEY,
    key: id,
    subKey: "salesBudget",
    value: salesBudget
  };
}

export function setFirstRunId(id, firstRunId) {
  return (dispatch, getState) => {
    dispatch(runReducer.pollJob(firstRunId, true));

    dispatch(resetOptimizer(id));

    return dispatch({
      type: UPDATE_SUB_KEY,
      key: id,
      subKey: "firstRunId",
      value: firstRunId
    });
  };
}

export function setSecondRunId(id, secondRunId) {
  return (dispatch, getState) => {
    dispatch(runReducer.pollJob(secondRunId, true));

    dispatch(resetOptimizer(id));

    return dispatch({
      type: UPDATE_SUB_KEY,
      key: id,
      subKey: "secondRunId",
      value: secondRunId
    });
  };
}

export function setMarketingRunId(id, marketingRunId) {
  return {
    type: UPDATE_SUB_KEY,
    key: id,
    subKey: "marketingRunId",
    value: marketingRunId
  };
}

export function setSalesRunId(id, salesRunId) {
  return {
    type: UPDATE_SUB_KEY,
    key: id,
    subKey: "salesRunId",
    value: salesRunId
  };
}

export function setOverallScale(id, overallScale) {
  return {
    type: UPDATE_SUB_KEY,
    key: id,
    subKey: "overallScale",
    value: overallScale
  };
}

export function setDrilldownValue(id, drilldownValue) {
  return {
    type: UPDATE_SUB_KEY,
    key: id,
    subKey: "drilldownValue",
    value: drilldownValue
  };
}

export function setDrilldownColumn(id, drilldownColumn) {
  return (dispatch, getState) => {
    dispatch({
      type: UPDATE_SUB_KEY,
      key: id,
      subKey: "drilldownColumn",
      value: drilldownColumn
    });

    return dispatch(initOptimizerObject(id));
  };
}

export function setSalesDrilldown(id, salesDrilldown) {
  return {
    type: UPDATE_SUB_KEY,
    key: id,
    subKey: "salesDrilldown",
    value: salesDrilldown
  };
}

export function setMarketingDrilldown(id, marketingDrilldown) {
  return {
    type: UPDATE_SUB_KEY,
    key: id,
    subKey: "marketingDrilldown",
    value: marketingDrilldown
  };
}

export function setOverallScaleFromRowCount(id, rowCount) {
  return (dispatch, getState) => {
    const cm = getMarketingConfusionMatrix(getState(), id, true);

    const overallScale = Number(rowCount) / Number(cm.getTotal());

    return dispatch(setOverallScale(id, overallScale));
  };
}

/* Action Creators */

export function resetOptimizer(id) {
  return (dispatch, getState) => {
    let optimizer = { ...getOptimizer(getState(), id) };

    optimizer.overallRuns = { marketing: {}, sales: {} };
    optimizer.combinedRuns = {};
    optimizer.marketingRuns = {};
    optimizer.salesRuns = {};
    optimizer.drilldownValue = undefined;
    optimizer.drilldownColumn = undefined;
    optimizer.salesDrilldown = undefined;
    optimizer.marketingDrilldown = undefined;

    return dispatch(setOptimizer(id, optimizer));
  };
}

export function initOptimizerObject(id) {
  return (dispatch, getState) => {
    let optimizer = { ...getOptimizer(getState(), id) };

    const firstDataset = setupReducer.getDatasetFromSetup(getState(), optimizer.firstRunId);

    optimizer.overallRuns = { marketing: {}, sales: {} };
    optimizer.combinedRuns = {};
    optimizer.marketingRuns = {};
    optimizer.salesRuns = {};

    firstDataset.fields[optimizer.drilldownColumn].data.forEach((v) => (optimizer.combinedRuns[v.value] = {}));

    return dispatch(setOptimizer(id, optimizer));
  };
}

export function pollJobs(id) {
  return (dispatch, getState) => {
    const optimizer = getOptimizer(getState(), id);

    const jobKeys = [];

    Object.keys(optimizer.overallRuns).forEach((v) =>
      Object.keys(optimizer.overallRuns[v]).forEach((p) => jobKeys.push(optimizer.overallRuns[v][p]))
    );

    Object.keys(optimizer.combinedRuns).forEach((v) =>
      Object.keys(optimizer.combinedRuns[v]).forEach((p) => jobKeys.push(optimizer.combinedRuns[v][p]))
    );

    Object.keys(optimizer.marketingRuns).forEach((v) => jobKeys.push(optimizer.marketingRuns[v]));

    Object.keys(optimizer.salesRuns).forEach((v) => jobKeys.push(optimizer.salesRuns[v]));

    jobKeys.forEach((k) => {
      if (Object.keys(runReducer.getJob(getState(), k)).length === 0) {
        dispatch(runReducer.pollJob(k, true));
      }
    });
  };
}

export function initOptimizerStorage() {
  return (dispatch, getState) => {
    const rawOptimizer = loginReducer.getItem(getState(), "aible_optimizers");
    const parsedOptimizer = rawOptimizer ? JSON.parse(rawOptimizer) : undefined;

    parsedOptimizer && dispatch({ type: INIT_OPTIMIZER, data: parsedOptimizer });
  };
}

export function saveOptimizerStorage() {
  return (dispatch, getState) => {
    const parsedOptimizer = getBase(getState());
    const rawOptimizer = JSON.stringify(parsedOptimizer);

    localStorage.setItem("aible_optimizers", rawOptimizer);
  };
}

export function handleCreate() {
  return (dispatch, getState) => {
    const state = getState();

    const firstRunId = optimizerCreatorReducer.getFirstRunId(state);
    const secondRunId = optimizerCreatorReducer.getSecondRunId(state);

    const id = getId(firstRunId, secondRunId);

    const overallColumn = optimizerCreatorReducer.getOverallColumn(state);
    const firstColumn = optimizerCreatorReducer.getFirstColumn(state);
    const secondColumn = optimizerCreatorReducer.getSecondColumn(state);

    const firstDataset = setupReducer.getDatasetFromSetup(state, firstRunId);
    const secondDataset = setupReducer.getDatasetFromSetup(state, secondRunId);

    let optimizer = {
      marketingRunId: firstRunId,
      salesRunId: secondRunId,
      firstRunId: firstRunId,
      secondRunId: secondRunId,
      drilldownColumn: overallColumn,
      marketingDrilldown: firstColumn,
      salesDrilldown: secondColumn,
      drilldownValue: "Overall",
      overallRuns: {
        marketing: {},
        sales: {}
      },
      marketingRuns: {},
      salesRuns: {},
      combinedRuns: {},
      salesBudget: "5220000000",
      marketingBudget: "938540000",
      overallScale: 1
    };

    if (
      firstDataset &&
      firstDataset.fields &&
      firstDataset.fields[overallColumn] &&
      firstDataset.fields[overallColumn].data &&
      firstDataset &&
      firstDataset.fields &&
      firstDataset.fields[firstColumn] &&
      firstDataset.fields[firstColumn].data &&
      secondDataset &&
      secondDataset.fields &&
      secondDataset.fields[secondColumn] &&
      secondDataset.fields[secondColumn].data
    ) {
      firstDataset.fields[overallColumn].data.forEach((c, i) => {
        dispatch(createRunFromValue(firstRunId, overallColumn, c.value)).then((val) => {
          optimizer.overallRuns.marketing[c.value] = val;

          // TODO: Set a counter maybe and only call this once?
          dispatch(setOptimizer(id, optimizer));
        });

        dispatch(createRunFromValue(secondRunId, overallColumn, c.value)).then((val) => {
          optimizer.overallRuns.sales[c.value] = val;

          dispatch(setOptimizer(id, optimizer));
        });

        optimizer.combinedRuns[c.value] = {};

        firstDataset.fields[firstColumn].data.forEach((s) => {
          dispatch(createRunFromValue(firstRunId, firstColumn, s.value)).then((val) => {
            optimizer.combinedRuns[c.value][s.value] = {
              isMarketing: true,
              runId: val
            };

            dispatch(setOptimizer(id, optimizer));
          });
        });

        secondDataset.fields[secondColumn].data.forEach((s) => {
          dispatch(createRunFromValue(secondRunId, secondColumn, s.value)).then((val) => {
            optimizer.combinedRuns[c.value][s.value] = {
              isMarketing: false,
              runId: val
            };

            dispatch(setOptimizer(id, optimizer));
          });
        });
      });

      firstDataset.fields[firstColumn].data.forEach((c) => {
        dispatch(createRunFromValue(firstRunId, firstColumn, c.value)).then((val) => {
          optimizer.marketingRuns[c.value] = val;

          dispatch(setOptimizer(id, optimizer));
        });
      });

      secondDataset.fields[secondColumn].data.map((c) => {
        dispatch(createRunFromValue(secondRunId, secondColumn, c.value)).then((val) => {
          optimizer.salesRuns[c.value] = val;

          dispatch(setOptimizer(id, optimizer));
        });
      });
    }
  };
}

export function createRunFromValue(runId, column, value) {
  return (dispatch, getState) => {
    const setup = setupReducer.getSetup(getState(), runId);

    const specialVals = [{ column: column, value: value, operand: "=" }];

    dispatch(setupReducer.createSetupFromSpecial(runId, specialVals));

    return dispatch(
      setupReducer.initJobFromWizard(runId, setup.blueprintId, {
        isNoRedirect: true
      })
    );
  };
}

export function hasLoadedAllJobs(state, id) {
  const optimizer = getOptimizer(state, id);

  if (optimizer !== undefined) {
    const jobKeys = getAllJobsFromOptimizer(state, optimizer.firstRunId, optimizer.secondRunId);

    let loadedCount = 0;

    jobKeys.forEach((k) => {
      if (Object.keys(runReducer.getJob(state, k)).length > 0) {
        loadedCount++;
      }
    });

    return loadedCount === jobKeys.length;
  }

  return false;
}

export function getAllJobsFromOptimizer(state, firstRunId, secondRunId) {
  const id = getId(firstRunId, secondRunId);

  const optimizer = getOptimizer(state, id);

  let jobKeys = [firstRunId, secondRunId];

  Object.keys(optimizer.overallRuns).forEach((v) =>
    Object.keys(optimizer.overallRuns[v]).forEach((p) => jobKeys.push(optimizer.overallRuns[v][p]))
  );

  Object.keys(optimizer.combinedRuns).forEach((v) =>
    Object.keys(optimizer.combinedRuns[v]).forEach((p) => jobKeys.push(optimizer.combinedRuns[v][p].runId))
  );

  Object.keys(optimizer.marketingRuns).forEach((v) => jobKeys.push(optimizer.marketingRuns[v]));

  Object.keys(optimizer.salesRuns).forEach((v) => jobKeys.push(optimizer.salesRuns[v]));

  return jobKeys;
}

export function init(firstRunId, secondRunId) {
  return (dispatch, getState) => {
    const id = getId(firstRunId, secondRunId);

    if (getOptimizer(getState(), id) === undefined) {
      dispatch(initOptimizerStorage());
    }

    const state = getState();

    const jobKeys = getAllJobsFromOptimizer(state, firstRunId, secondRunId);

    /*

    let promises = [];
    let jobs = [];

    jobKeys.forEach(
      k =>
        (runReducer.getJob(getState(), k) === undefined ||
          Object.keys(runReducer.getJob(getState(), k)).length === 0) &&
        promises.push(ModelAPI.getRun(k).then(data => jobs.push(data)))
    );

    Promise.all(promises).then(() => {
      if (jobs && jobs.length > 0) {
        return dispatch(runReducer.updateJobs(jobs));
      }
    });

    */

    jobKeys.forEach((k) => {
      if (Object.keys(runReducer.getJob(getState(), k)).length === 0) {
        dispatch(runReducer.pollJob(k, true));
      }
    });

    return Promise.resolve();
  };
}

export default optimizerReducer;
